import { ref } from 'vue';

// Δεν υπάρχει ακόμα πραγματικό backend για αποθήκευση αρχείων, οπότε τα αρχεία (μαζί με το
// binary περιεχόμενό τους) αποθηκεύονται τοπικά στον browser μέσω IndexedDB — έτσι επιβιώνουν
// σε refresh, σε αντίθεση με ένα απλό in-memory state. Module-level singleton, κοινόχρηστο ανάμεσα
// στο FileManagementMenu.vue (ανέβασμα/αρχειοθέτηση) και το Clients.vue (στήλη "Documents"),
// ώστε να βλέπουν τα ίδια δεδομένα.
const DB_NAME = 'mis-file-management';
const STORE_NAME = 'documents';
const DB_VERSION = 1;

let dbPromise = null;

function openDb() {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                if (!request.result.objectStoreNames.contains(STORE_NAME)) {
                    request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
    return dbPromise;
}

async function dbGetAll() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
}

async function dbPut(record) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

async function dbDelete(id) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

const documents = ref([]);
let nextDocId = 1;

// Φορτώνει τα ήδη αποθηκευμένα έγγραφα από το IndexedDB μία φορά, με το πρώτο import του module.
const ready = dbGetAll()
    .then((records) => {
        documents.value = records;
        nextDocId = records.reduce((max, d) => Math.max(max, d.id), 0) + 1;
    })
    .catch(() => {
        // IndexedDB μη διαθέσιμο (π.χ. private browsing) -> συνεχίζουμε με άδεια, in-memory λίστα.
        documents.value = [];
    });

async function addDocument(doc) {
    await ready;
    const id = nextDocId++;
    const record = { id, ...doc };
    documents.value.push(record);
    await dbPut(record);
    return id;
}

async function removeDocument(id) {
    documents.value = documents.value.filter((d) => d.id !== id);
    await dbDelete(id);
}

function documentsForClient(clientId) {
    if (!clientId) return [];
    // Το clientId μπαίνει άλλοτε ως number (API) και άλλοτε ως string (Select2 / route query),
    // οπότε συγκρίνουμε ως string ώστε να ταιριάζουν ανεξαρτήτως τύπου.
    return documents.value.filter((d) => String(d.clientId) === String(clientId));
}

// Cache των object URLs (blob:...) ώστε να μη δημιουργείται νέο URL σε κάθε re-render· τα URLs
// ζουν όσο η σελίδα (ελευθερώνονται αυτόματα από τον browser στο unload).
const objectUrlCache = new Map();
function documentDownloadUrl(doc) {
    // Πραγματικό αρχείο αποθηκευμένο στο δίσκο (public/client-documents, βλ. uploadToLocalDisk)
    // -> στατικό, μόνιμο URL, διαθέσιμο άμεσα χωρίς να χρειάζεται το blob.
    if (doc?.url) return doc.url;
    if (!doc?.blob) return '#';
    if (!objectUrlCache.has(doc.id)) {
        objectUrlCache.set(doc.id, URL.createObjectURL(doc.blob));
    }
    return objectUrlCache.get(doc.id);
}

export function useDocumentsStore() {
    return { documents, addDocument, removeDocument, documentsForClient, documentDownloadUrl };
}
